import { useEffect, useRef } from "react";
import type { SalesMove } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { formatDue, formatMoney, priorityLabel } from "./format";

type Props = {
  move: SalesMove;
  selected?: boolean;
  onSelect: () => void;
};

export function SalesMoveCard({ move, selected, onSelect }: Props) {
  const cardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!selected) return;
    cardRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: "smooth",
    });
  }, [selected, move.id]);

  const meta = [
    move.accountName,
    move.relevantPerson?.name,
    move.relevantPerson?.role,
  ]
    .filter(Boolean)
    .join(" · ");

  const dealLine = [
    move.dealStage,
    typeof move.dealValue === "number"
      ? formatMoney(move.dealValue, move.currency)
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full rounded-lg border px-3.5 py-3 text-left transition",
        selected
          ? "border-brand/50 bg-brand/5"
          : "border-border bg-card hover:bg-muted/15",
      )}
    >
      <p className="text-sm font-semibold text-foreground">{move.recommendedAction}</p>
      {meta ? (
        <p className="mt-1 truncate text-xs text-muted-foreground">{meta}</p>
      ) : null}
      {dealLine ? (
        <p className="mt-0.5 text-xs text-muted-foreground">{dealLine}</p>
      ) : null}
      <p className="mt-2 line-clamp-2 text-xs text-foreground/80">{move.whyNow}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        {move.dueAt ? <span>{formatDue(move.dueAt)}</span> : null}
        <span
          className={cn(
            "rounded px-1.5 py-0.5 font-medium",
            move.priority === "critical"
              ? "bg-red-500/15 text-red-400"
              : move.priority === "high"
                ? "bg-amber-500/15 text-amber-400"
                : "bg-muted text-muted-foreground",
          )}
        >
          {priorityLabel(move.priority)}
        </span>
      </div>
    </button>
  );
}
